/**
 * Same-day dispatch message of the market reassurance lines (<market-usps>):
 * "Order now, it ships today", "Order within 1h 05min, it ships today", ...
 *
 * Orders ship the same day Monday to Friday (shop time zone, Paris by default), except on French
 * public holidays, when placed before the Standard cut-off, or before the Express cut-off for markets
 * that have an Express rate. A countdown is only shown close to a cut-off, and nothing is shown in
 * the very last minutes so we never promise a dispatch that can no longer happen.
 *
 * Call `element.update(new Date('2026-10-13T11:30:00+02:00'))` from the console to preview any date
 * (the shared timer goes back to the real time on its next tick).
 */
(() => {
  if (window.customElements.get('market-usps')) return;

  const TICK = 15000;
  const instances = new Set();
  const formatters = new Map();
  const holidayCache = new Map();
  let timer = null;

  // Wall-clock date and time in the given time zone, as plain numbers.
  const zonedParts = (date, timeZone) => {
    if (!formatters.has(timeZone)) {
      const options = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: false };
      let formatter;
      try {
        formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone });
      } catch (error) {
        formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'Europe/Paris' });
      }
      formatters.set(timeZone, formatter);
    }

    const parts = formatters.get(timeZone).formatToParts(date);
    const get = (type) => parseInt(parts.find((part) => part.type === type).value, 10);

    // Some engines return 24 for midnight with hour12: false.
    return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') % 24, minute: get('minute'), second: get('second') };
  };

  // French public holidays of a year as "month-day" keys (Easter: anonymous Gregorian algorithm).
  const frenchHolidays = (year) => {
    if (holidayCache.has(year)) return holidayCache.get(year);

    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const easterMonth = Math.floor((h + l - 7 * m + 114) / 31);
    const easterDay = ((h + l - 7 * m + 114) % 31) + 1;

    // Easter Monday, Ascension and Whit Monday, computed in UTC to avoid any DST shift.
    const fromEaster = (offset) => {
      const date = new Date(Date.UTC(year, easterMonth - 1, easterDay + offset));
      return `${date.getUTCMonth() + 1}-${date.getUTCDate()}`;
    };

    const holidays = new Set(['1-1', '5-1', '5-8', '7-14', '8-15', '11-1', '11-11', '12-25', fromEaster(1), fromEaster(39), fromEaster(50)]);
    holidayCache.set(year, holidays);
    return holidays;
  };

  // "41min" or "1h 05min".
  const formatDuration = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${String(minutes).padStart(2, '0')}min` : `${minutes}min`;
  };

  const tick = () => instances.forEach((instance) => instance.update());

  class MarketUsps extends HTMLElement {
    connectedCallback() {
      this.dispatch = this.querySelector('[data-dispatch]');
      this.dispatchText = this.querySelector('[data-dispatch-text]');
      instances.add(this);
      this.update();

      if (!timer) timer = setInterval(tick, TICK);
    }

    disconnectedCallback() {
      instances.delete(this);

      if (instances.size === 0 && timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    // Numeric data attribute, with a fallback when it is missing or invalid.
    number(name, fallback) {
      const value = parseFloat(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    // Which dispatch message applies at `now`, and how many seconds are left before its cut-off.
    evaluate(now) {
      const { year, month, day, hour, minute, second } = zonedParts(now, this.dataset.timeZone || 'Europe/Paris');
      const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();

      if (weekday === 0 || weekday === 6 || frenchHolidays(year).has(`${month}-${day}`)) return { state: 'none' };

      // Everything is compared in seconds since midnight, shop wall-clock time.
      const seconds = hour * 3600 + minute * 60 + second;
      const standard = this.number('cutoffStandard', 13) * 3600;
      const express = this.number('cutoffExpress', 16) * 3600;
      const countdownWindow = this.number('countdownWindow', 120) * 60;
      const countdownHide = this.number('countdownHide', 10) * 60;

      // The earliest cut-off still ahead wins: Standard first, then Express if the market has it.
      let left;
      let suffix;

      if (seconds < standard) {
        left = standard - seconds;
        suffix = '';
      } else if (this.dataset.express !== 'false' && seconds < express) {
        left = express - seconds;
        suffix = '-express';
      } else {
        return { state: 'none' };
      }

      if (left <= countdownHide) return { state: 'none' };
      if (left <= countdownWindow) return { state: `countdown${suffix}`, left };

      return { state: suffix ? 'express' : 'now' };
    }

    update(now = new Date()) {
      const { state, left } = this.evaluate(now);
      const messages = {
        now: this.dataset.msgNow,
        express: this.dataset.msgExpress,
        countdown: this.dataset.msgCountdown,
        'countdown-express': this.dataset.msgCountdownExpress
      };
      // Rounded up to the next minute so we never announce less time than is really left.
      const text = state === 'none' ? '' : (messages[state] || '').replace('[time]', formatDuration(Math.ceil(left / 60) || 0));
      const active = text !== '';

      this.setData('dispatchState', active ? state : 'none');
      this.setData('dispatchActive', String(active));

      if (this.dispatchText && this.dispatchText.textContent !== text) this.dispatchText.textContent = text;
      if (this.dispatch && this.dispatch.hidden === active) this.dispatch.hidden = !active;
    }

    setData(name, value) {
      if (this.dataset[name] !== value) this.dataset[name] = value;
    }
  }

  // Refresh as soon as the tab is visible again (timers are throttled in background tabs).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tick();
  });

  window.customElements.define('market-usps', MarketUsps);
})();
